import React, { useMemo, useState } from 'react';
import { Box, FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import AppFormSelectWrapper from '../../../components/designe/app-form-select-wrapper';
import Button from '../../../components/designe/app-button/AppButton';
import {
  AnalyticsAction,
  AnalyticsPeriod,
} from '../../../types/interfaces/analytics';
import { useLazyGetUsageEventsQuery } from '../../../services/redux/api/analyticsAdminApi';

type ChartRow = { at: string } & Partial<Record<AnalyticsAction, number>>;

const PERIOD_OPTIONS: Array<{ id: AnalyticsPeriod; name: string }> = [
  { id: 'week', name: 'Week' },
  { id: 'month', name: 'Month' },
  { id: 'quarter', name: 'Quarter' },
  { id: 'year', name: 'Year' },
];

const ACTION_OPTIONS: Array<{ id: AnalyticsAction | 'all'; name: string }> = [
  { id: 'all', name: 'All actions' },
  { id: 'signUp', name: 'signUp' },
  { id: 'signIn', name: 'signIn' },
  { id: 'trainingCreated', name: 'trainingCreated' },
  { id: 'trainingSetCreated', name: 'trainingSetCreated' },
  { id: 'programCollectionCreated', name: 'programCollectionCreated' },
  { id: 'programItemCreated', name: 'programItemCreated' },
  { id: 'exerciseCreated', name: 'exerciseCreated' },
  { id: 'trainingStarted', name: 'trainingStarted' },
  { id: 'trainingGenerated', name: 'trainingGenerated' },
];

const CHART_COLORS = [
  '#1976d2',
  '#2e7d32',
  '#ed6c02',
  '#9c27b0',
  '#d32f2f',
  '#00838f',
  '#5d4037',
  '#455a64',
  '#7b1fa2',
];

function toUtcDayStart(date: Date) {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );
}

function toUtcMonthStart(date: Date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function getPeriodBuckets(period: AnalyticsPeriod) {
  const now = new Date();
  const buckets: string[] = [];

  if (period === 'year') {
    const currentMonthStart = toUtcMonthStart(now);
    const start = new Date(currentMonthStart);
    start.setUTCMonth(start.getUTCMonth() - 11);

    const cursor = new Date(start);
    while (cursor <= currentMonthStart) {
      buckets.push(cursor.toISOString());
      cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    }

    return buckets;
  }

  const periodDays = {
    week: 7,
    month: 30,
    quarter: 90,
  }[period];

  const end = toUtcDayStart(now);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - (periodDays - 1));

  const cursor = new Date(start);
  while (cursor <= end) {
    buckets.push(cursor.toISOString());
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return buckets;
}

function normalizePointBucket(at: string, period: AnalyticsPeriod) {
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) {
    return at;
  }

  return (
    period === 'year' ? toUtcMonthStart(date) : toUtcDayStart(date)
  ).toISOString();
}

function formatBucketLabel(value: string, period: AnalyticsPeriod) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('default', {
    day: period === 'year' ? undefined : '2-digit',
    month: '2-digit',
    year: '2-digit',
    timeZone: 'UTC',
  }).format(date);
}

export function UsageAnalytics() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<AnalyticsPeriod>('month');
  const [action, setAction] = useState<AnalyticsAction | 'all'>('all');
  const [unique, setUnique] = useState(false);
  const [loadUsageEvents, { data, isFetching }] = useLazyGetUsageEventsQuery();

  const formikLike = {
    values: { period, action },
    touched: {},
    errors: {},
    handleBlur: () => undefined,
    handleChange: () => undefined,
    setFieldValue: () => undefined,
  };

  const lines = useMemo(() => data?.series ?? [], [data]);

  const chartData = useMemo<ChartRow[]>(() => {
    if (!data?.series?.length) {
      return [];
    }

    const buckets = getPeriodBuckets(data.period);
    const grouped = new Map<string, ChartRow>();
    const actions = data.series.map((item) => item.action);

    buckets.forEach((bucket) => {
      const row: ChartRow = { at: bucket };
      actions.forEach((currentAction) => {
        row[currentAction] = 0;
      });
      grouped.set(bucket, row);
    });

    data.series.forEach((series) => {
      series.points.forEach((point) => {
        const pointBucket = normalizePointBucket(point.at, data.period);
        const row = grouped.get(pointBucket) ?? { at: pointBucket };
        row[series.action] = point.value;
        grouped.set(pointBucket, row);
      });
    });

    return Array.from(grouped.values()).sort((a, b) =>
      a.at.localeCompare(b.at),
    );
  }, [data]);

  const hasData = chartData.length > 0 && lines.length > 0;

  async function handleRequest() {
    await loadUsageEvents({
      period,
      unique,
      actions: action === 'all' ? undefined : [action],
    });
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <AppFormSelectWrapper
            name="action"
            label={t('common.actions')}
            options={ACTION_OPTIONS}
            formik={formikLike}
            disableClear
            onChange={(e) =>
              setAction(
                (e.target.value?.id ?? 'all') as AnalyticsAction | 'all',
              )
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <AppFormSelectWrapper
            name="period"
            label={t('common.period')}
            options={PERIOD_OPTIONS}
            formik={formikLike}
            disableClear
            disableSorting
            onChange={(e) =>
              setPeriod((e.target.value?.id ?? 'month') as AnalyticsPeriod)
            }
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <FormControlLabel
            control={
              <Switch
                checked={unique}
                onChange={(_event, checked) => setUnique(checked)}
              />
            }
            label={t('adminDashboard.analytics.uniqueUsers')}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <Button fullWidth onClick={handleRequest} loader={isFetching}>
            {t('adminDashboard.analytics.request')}
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, width: '100%', height: 460 }}>
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 16, right: 24, bottom: 8, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="at"
                tickFormatter={(value) => formatBucketLabel(value, data.period)}
              />
              <YAxis allowDecimals={false} />
              <Tooltip
                formatter={(value) => [value, t('common.value')]}
                labelFormatter={(value) =>
                  formatBucketLabel(value, data.period)
                }
              />
              <Legend />
              {lines.map((series, index) => (
                <Line
                  key={series.action}
                  type="monotone"
                  dataKey={series.action}
                  stroke={CHART_COLORS[index % CHART_COLORS.length]}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="text-md">
            {t('adminDashboard.analytics.empty')}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
